import moment from 'moment'
import { Options } from './types'
import { ranges } from './constants'

export interface PresetInfo {
	dropdownValue: Options
	chipLabel: string
}

export const getPresetInfo = (from: Date, to: Date): PresetInfo => {
	if (isLast90Days(from, to))
		return { dropdownValue: 'Last 90 days', chipLabel: 'Last 90 days' }
	if (isLast30Days(from, to))
		return { dropdownValue: 'Last 30 days', chipLabel: 'Last 30 days' }
	if (isLast7Days(from, to))
		return { dropdownValue: 'Last 7 days', chipLabel: 'Last 7 days' }

	const weekOffset = getWeekOffset(from, to)
	if (weekOffset === 0)
		return { dropdownValue: 'This week', chipLabel: 'This week' }
	if (weekOffset === 1)
		return { dropdownValue: 'Last week', chipLabel: 'Last week' }
	if (weekOffset !== null && weekOffset >= 2)
		return { dropdownValue: 'Custom', chipLabel: ordinalLabel(weekOffset, 'week') }

	const monthOffset = getMonthOffset(from, to)
	if (monthOffset === 0)
		return { dropdownValue: 'This month', chipLabel: 'This month' }
	if (monthOffset === 1)
		return { dropdownValue: 'Last month', chipLabel: 'Last month' }
	if (monthOffset !== null && monthOffset >= 2)
		return { dropdownValue: 'Custom', chipLabel: ordinalLabel(monthOffset, 'month') }

	return { dropdownValue: 'Custom', chipLabel: 'Custom' }
}

export const getDateTexts = (from: Date, to: Date) => {
	const info = getPresetInfo(from, to)
	if (info.dropdownValue !== 'Custom') {
		return { value: info.dropdownValue, label: info.dropdownValue }
	}
	return {
		value: 'Custom' as Options,
		label: `${moment(from).format('LL')} - ${moment(to).format('LL')}`
	}
}

export const shiftRange = (
	from: Date,
	to: Date,
	direction: -1 | 1
): { startDate: Date; endDate: Date } | null => {
	const weekOffset = getWeekOffset(from, to)
	if (weekOffset !== null) {
		const newOffset = weekOffset - direction
		if (newOffset < 0) return null
		return rangeForWeekOffset(newOffset)
	}

	const monthOffset = getMonthOffset(from, to)
	if (monthOffset !== null) {
		const newOffset = monthOffset - direction
		if (newOffset < 0) return null
		return rangeForMonthOffset(newOffset)
	}

	const dayMs = 24 * 60 * 60 * 1000
	const durationDays = Math.floor((to.getTime() - from.getTime()) / dayMs) + 1
	const shiftMs = direction * durationDays * dayMs
	return {
		startDate: new Date(from.getTime() + shiftMs),
		endDate: new Date(to.getTime() + shiftMs)
	}
}

export const canShiftNext = (from: Date, to: Date): boolean => {
	const weekOffset = getWeekOffset(from, to)
	if (weekOffset !== null) return weekOffset > 0
	const monthOffset = getMonthOffset(from, to)
	if (monthOffset !== null) return monthOffset > 0
	return true
}

const isLast90Days = (from: Date, to: Date) =>
	matchesFixedRange(from, to, ranges['Last 90 days'])
const isLast30Days = (from: Date, to: Date) =>
	matchesFixedRange(from, to, ranges['Last 30 days'])
const isLast7Days = (from: Date, to: Date) =>
	matchesFixedRange(from, to, ranges['Last 7 days'])

const matchesFixedRange = (
	from: Date,
	to: Date,
	target: { startDate: Date; endDate: Date }
) =>
	moment(from).isSame(target.startDate, 'day') &&
	moment(to).isSame(target.endDate, 'day')

const getWeekOffset = (from: Date, to: Date): number | null => {
	const fromM = moment(from)
	const toM = moment(to)
	const today = moment().endOf('day')

	if (
		fromM.isSame(moment().startOf('week'), 'day') &&
		toM.isSame(today, 'day')
	) {
		return 0
	}

	if (!fromM.isSame(fromM.clone().startOf('week'), 'day')) return null
	if (!toM.isSame(toM.clone().endOf('week'), 'day')) return null
	if (!fromM.isSame(toM, 'week')) return null

	const weeksDiff = moment().startOf('week').diff(fromM, 'weeks')
	return weeksDiff >= 1 ? weeksDiff : null
}

const getMonthOffset = (from: Date, to: Date): number | null => {
	const fromM = moment(from)
	const toM = moment(to)
	const today = moment().endOf('day')

	if (
		fromM.isSame(moment().startOf('month'), 'day') &&
		toM.isSame(today, 'day')
	) {
		return 0
	}

	if (!fromM.isSame(fromM.clone().startOf('month'), 'day')) return null
	if (!toM.isSame(toM.clone().endOf('month'), 'day')) return null
	if (!fromM.isSame(toM, 'month')) return null

	const monthsDiff = moment().startOf('month').diff(fromM, 'months')
	return monthsDiff >= 1 ? monthsDiff : null
}

const rangeForWeekOffset = (offset: number) => {
	if (offset === 0) {
		return {
			startDate: moment().startOf('week').toDate(),
			endDate: moment().endOf('day').toDate()
		}
	}
	return {
		startDate: moment().subtract(offset, 'weeks').startOf('week').toDate(),
		endDate: moment().subtract(offset, 'weeks').endOf('week').toDate()
	}
}

const rangeForMonthOffset = (offset: number) => {
	if (offset === 0) {
		return {
			startDate: moment().startOf('month').toDate(),
			endDate: moment().endOf('day').toDate()
		}
	}
	return {
		startDate: moment().subtract(offset, 'months').startOf('month').toDate(),
		endDate: moment().subtract(offset, 'months').endOf('month').toDate()
	}
}

const ORDINAL_WORDS = [
	'',
	'',
	'Two',
	'Three',
	'Four',
	'Five',
	'Six',
	'Seven',
	'Eight',
	'Nine',
	'Ten'
]

const ordinalLabel = (n: number, unit: 'week' | 'month'): string => {
	const word = ORDINAL_WORDS[n] || `${n}`
	return `${word} ${unit}s ago`
}
